import { useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";

interface UseScrollToTopOptions {
  behavior?: ScrollBehavior;
  delay?: number;
  shouldScroll?: boolean;
  onScrollComplete?: () => void;
}

export function useScrollToTop(options: UseScrollToTopOptions = {}) {
  const { behavior = "smooth", delay = 0, shouldScroll = true, onScrollComplete } = options;
  const location = useLocation();

  const scrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior });
    onScrollComplete?.();
  }, [behavior, onScrollComplete]);

  useEffect(() => {
    if (!shouldScroll) return;

    if (delay > 0) {
      const timeoutId = setTimeout(scrollToTop, delay);
      return () => clearTimeout(timeoutId);
    } else {
      scrollToTop();
    }
    // Only scroll on pathname changes. Search param changes (e.g. table filters)
    // should not reset scroll position.
  }, [location.pathname, shouldScroll, delay, scrollToTop]);
}

// Hook for scrolling on state changes (e.g., form steps)
export function useScrollToTopOnChange(dependencies: any[], options: UseScrollToTopOptions = {}) {
  const { behavior = "smooth", delay = 0, shouldScroll = true, onScrollComplete } = options;

  const scrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior });
    onScrollComplete?.();
  }, [behavior, onScrollComplete]);

  useEffect(() => {
    if (!shouldScroll) return;

    if (delay > 0) {
      const timeoutId = setTimeout(scrollToTop, delay);
      return () => clearTimeout(timeoutId);
    } else {
      scrollToTop();
    }
  }, [dependencies, shouldScroll, delay, scrollToTop]);
}
